import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import { deletePromotionRule, getPromotionRules, PromotionRule, PromotionRuleInput, PromotionRules, savePromotionRule } from '../../api';
import { categoryLabel } from '../../features';

const goldButton = { background: 'linear-gradient(135deg, #c9a227, #dbb42c)', color: '#000', fontWeight: 700 };
const field = { input: { color: '#e6e6e6' }, '& .MuiSelect-select': { color: '#e6e6e6' }, textarea: { color: '#e6e6e6' } };
const labelStyle = { style: { color: '#8b8b95' } };
const errorText = (err: unknown) => (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Could not save the rule.';

const place = (r: { countryName?: string; country: string; state?: string | null }) => (r.state ? `${r.state}, ${r.country}` : r.countryName ?? r.country);

// Where offers may be promoted, by country and by state. A state rule replaces its country's rule there;
// with no rule, offers are on and alcohol is off. The global switches above still apply on top.
const RegionControls: React.FC = () => {
  const [data, setData] = useState<PromotionRules | null>(null);
  const [editing, setEditing] = useState<(PromotionRuleInput & { wasAlcohol: boolean }) | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPromotionRules().then(setData).catch(() => setMessage({ ok: false, text: 'Could not load the promotion rules.' }));
  }, []);

  const edit = (r: PromotionRule) => setEditing({
    country: r.country, state: r.state ?? null, offersEnabled: r.offersEnabled, alcoholOffers: r.alcoholOffers,
    disabledCategories: r.disabledCategories, note: r.note ?? '', reason: '', wasAlcohol: r.alcoholOffers
  });

  // A new state rule starts from its country's rule, so only the differences need changing.
  const startNew = () => {
    const india = data?.rules.find((r) => r.country === 'IN' && !r.state);
    setEditing({ country: 'IN', state: '', offersEnabled: india?.offersEnabled ?? true, alcoholOffers: false, disabledCategories: india?.disabledCategories ?? [], note: '', reason: '', wasAlcohol: false });
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    setMessage(null);
    try {
      const { wasAlcohol, ...rule } = editing;
      setData(await savePromotionRule({ ...rule, state: rule.state || null, note: rule.note || null, reason: rule.reason || undefined }));
      setMessage({ ok: true, text: `Saved the rule for ${place({ country: rule.country, state: rule.state, countryName: data?.countries[rule.country] })}.` });
      setEditing(null);
    } catch (err) {
      setMessage({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: PromotionRule) => {
    const fallback = r.state ? `${r.countryName}'s rule` : 'the default (offers on, alcohol off)';
    if (!window.confirm(`Remove the rule for ${place(r)}? Venues there will follow ${fallback}.`)) return;
    setBusy(true);
    try {
      setData(await deletePromotionRule(r.id));
      setMessage({ ok: true, text: `Removed the rule for ${place(r)}.` });
    } catch (err) {
      setMessage({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const turningAlcoholOn = !!editing && editing.alcoholOffers && !editing.wasAlcohol;

  return (
    <Box sx={{ border: '1px solid #2a2f38', borderRadius: 2, p: 2.5, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={1}>
        <Box>
          <Typography variant="h6" color="#e6e6e6">Promotion by Country / State</Typography>
          <Typography variant="body2" color="#8b8b95" maxWidth={720}>
            Choose where offers may be promoted. A state rule overrides its country's rule. Places without a rule show offers but never alcohol.
            Alcohol also needs the global switches above and a verified venue licence. Changes are in the Audit Log.
          </Typography>
        </Box>
        <Button sx={goldButton} onClick={startNew} disabled={!data || busy}>Add rule</Button>
      </Box>
      {message && <Alert severity={message.ok ? 'success' : 'error'} sx={{ mb: 1.5 }}>{message.text}</Alert>}

      {editing && data && (
        <Box sx={{ border: '1px solid #4b3a12', borderRadius: 2, p: 2, mb: 2, background: 'rgba(201,162,39,0.05)' }}>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={1}>
            <TextField select label="Country" value={editing.country} onChange={(e) => setEditing({ ...editing, country: e.target.value, state: '' })} sx={field} InputLabelProps={labelStyle}>
              {Object.entries(data.countries).map(([code, name]) => <MenuItem key={code} value={code}>{name} ({code})</MenuItem>)}
            </TextField>
            {editing.country === 'IN' ? (
              <TextField select label="State (empty = whole country)" value={editing.state ?? ''} onChange={(e) => setEditing({ ...editing, state: e.target.value })} sx={field} InputLabelProps={labelStyle}>
                <MenuItem value="">Whole country</MenuItem>
                {data.indianStates.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            ) : (
              <TextField label="State / province (empty = whole country)" value={editing.state ?? ''} onChange={(e) => setEditing({ ...editing, state: e.target.value })} sx={field} InputLabelProps={labelStyle} inputProps={{ maxLength: 60 }} />
            )}
          </Box>
          <FormControlLabel control={<Switch checked={editing.offersEnabled} onChange={(e) => setEditing({ ...editing, offersEnabled: e.target.checked })} />}
            label={<span style={{ color: '#e6e6e6' }}>Show offers here</span>} />
          <FormControlLabel control={<Switch checked={editing.alcoholOffers} disabled={!editing.offersEnabled} onChange={(e) => setEditing({ ...editing, alcoholOffers: e.target.checked })} />}
            label={<span style={{ color: '#e6e6e6' }}>Allow alcohol offers and wine shops</span>} />
          <Typography variant="body2" color="#8b8b95" mt={1}>Categories switched off here:</Typography>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }}>
            {data.categories.map((c) => (
              <FormControlLabel key={c} label={<span style={{ color: '#e6e6e6' }}>{categoryLabel(c)}</span>}
                control={<Checkbox size="small" checked={editing.disabledCategories.includes(c)} disabled={!editing.offersEnabled}
                  onChange={(e) => setEditing({ ...editing, disabledCategories: e.target.checked ? [...editing.disabledCategories, c] : editing.disabledCategories.filter((x) => x !== c) })} />} />
            ))}
          </Box>
          <TextField fullWidth label="Note (why this rule exists)" value={editing.note ?? ''} onChange={(e) => setEditing({ ...editing, note: e.target.value })} sx={{ ...field, mt: 1.5 }} InputLabelProps={labelStyle} inputProps={{ maxLength: 300 }} />
          {turningAlcoholOn && (
            <TextField fullWidth required label="Legal approval reference (required to allow alcohol)" value={editing.reason ?? ''} onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
              sx={{ ...field, mt: 1.5 }} InputLabelProps={labelStyle} />
          )}
          <Box display="flex" gap={1} mt={2}>
            <Button sx={goldButton} onClick={save} disabled={busy || (turningAlcoholOn && !editing.reason?.trim())}>Save rule</Button>
            <Button onClick={() => setEditing(null)} disabled={busy} sx={{ color: '#e6e6e6' }}>Cancel</Button>
          </Box>
        </Box>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', color: '#e6e6e6', fontSize: 14 }}>
          <thead>
            <tr style={{ color: '#8b8b95', textAlign: 'left' }}>
              {['Place', 'Venues', 'Offers', 'Alcohol', 'Categories off', 'Note', ''].map((h) => <th key={h} style={{ padding: '8px 6px', borderBottom: '1px solid #2a2f38' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(data?.rules ?? []).map((r) => (
              <tr key={r.id}>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #1f232b', fontWeight: r.state ? 400 : 700 }}>{place(r)}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #1f232b' }}>{r.venues}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #1f232b', color: r.offersEnabled ? '#7bd88f' : '#ff8a8a' }}>{r.offersEnabled ? 'On' : 'Off'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #1f232b', color: r.alcoholOffers && r.offersEnabled ? '#7bd88f' : '#ff8a8a' }}>{r.alcoholOffers && r.offersEnabled ? 'Allowed' : 'Off'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #1f232b' }}>{r.disabledCategories.length ? r.disabledCategories.map(categoryLabel).join(', ') : '–'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #1f232b', color: '#8b8b95', maxWidth: 280 }}>{r.note ?? ''}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid #1f232b', whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => edit(r)} disabled={busy} sx={{ color: '#dbb42c' }}>Edit</Button>
                  <Button size="small" onClick={() => remove(r)} disabled={busy} sx={{ color: '#ff8a8a' }}>Remove</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default RegionControls;
